<template>
  <section class="ma-4 ma-md-8">
    <t-floating-bar v-if="eventTitle">
      {{ eventTitle }}
    </t-floating-bar>
    <t-page-title
      title="Acessos | Leitura Detalhada"
      :on-report="() => datatable?.openDialogReport()"
    >
      <template v-if="selectedEvent !== null" #prepend-custom-action>
        <import-readings-action
          v-if="can('create', 'readings')"
          :event="selectedEvent"
          @import="datatable?.search()"
        />
        <clear-readings-action
          v-if="can('delete', 'readings')"
          :event="selectedEvent"
          @clear="_window.location.reload()"
        />
      </template>
    </t-page-title>
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :report="report"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :pagination="pagination"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.sale.holder`]="{ item }">
            <span>{{ item.sale.order_item?.name.toLocaleUpperCase() ?? item.sale.holder?.toLocaleUpperCase() }}</span>
          </template>
          <template #[`item.sale.order_item.order.owner.name`]="{ item }">
            <span>{{ item.sale.order_item?.order?.owner?.name.toLocaleUpperCase() ?? '' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue'
import type { AxiosResponse } from 'axios'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import EventService from '@/services/EventService'
import ReadingService from '@/services/ReadingService'
import type {
  DataTableFilter,
  DataTableHeader,
  DataTablePagination,
} from '@/types/DataTable'
import type { Report, ReportItem } from '@/types/Report'
import type PaginatedResponse from '@/types/PaginatedResponse'
import type Reading from '@/types/Reading'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import { useSnackBar } from '@/stores/snackbar'
import ClearReadingsAction from './clear-readings-action.vue'
import ImportReadingsAction from './import-readings-action.vue'
import { useAbility } from '@casl/vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useProducerStore } from '@/stores/producer'

const snackbar = useSnackBar()

const _window = window

const { can } = useAbility()

const datatable = useTemplateRef<DataTableType>('datatable')

const resource = 'reports'

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  {
    title: 'Código',
    value: 'sale.code',
    key: 'sale.code',
    sortable: false,
  },
  {
    title: 'Nome do portador',
    value: 'sale.holder',
    key: 'sale.holder',
    sortable: false,
  },
  {
    title: 'Títular',
    value: 'sale.order_item.order.owner.name',
    key: 'sale.order_item.order.owner.name',
    sortable: false,
  },
  {
    title: 'Setor',
    value: 'passport.sector.name',
    key: 'passport.sector.name',
    sortable: false,
  },
  {
    title: 'Tipo de ingresso',
    value: 'passport.ticket_type.name',
    key: 'passport.ticket_type.name',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Horário da leitura',
    value: 'created_at',
    key: 'created_at',
    sortable: false,
    align: 'center',
  },
]

const pagination: DataTablePagination = {
  page: 1,
  itemsPerPage: 100,
  total: 0,
  totalPages: 0,
}

const report: Ref<Report> = ref({
  items: [
    { label: 'ID', field: 'id', active: false, mandatory: false },
    { label: 'Código', field: 'sale.code', active: false, mandatory: false },
    { label: 'Número do pedido', field: 'sale.order_item.order.order_number', active: false, mandatory: false },
    { label: 'Nome do portador', field: 'sale.holder', active: false },
    { label: 'CPF do portador', field: 'sale.document', active: false },
    { label: 'Títular', field: 'sale.order_item.order.owner.name', active: false },
    { label: 'Setor', field: 'passport.sector.name', active: false },
    { label: 'Tipo de ingresso', field: 'passport.ticket_type.name', active: false },
    { label: 'Horário da leitura', field: 'created_at', active: false },
  ] as ReportItem[],
  filename: 'relatorio_leituras',
  provider: async (filters: string, selected) => {
    return await new ReadingService().report(filters, selected)
  },
})

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
})

// sector_id/type_id keep a "Todos" option and are only ever populated once,
// right after the event resolves - not reactively on every later event
// change (the original's filterProvider ran once, guarded by
// hasFilterProviderRun, with no watcher on event_id). Constructed directly
// since useSectorFilter/useTicketTypeFilter both reactively reload on every
// event change and don't offer a "Todos" option, neither of which matches
// this file's original behavior.
const sectorItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const sectorModel: Ref<string | null> = ref('')
const typeItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const typeModel: Ref<string | null> = ref('')
const sectorTypeReady = Promise.withResolvers<boolean>()

const sectorFilter: DataTableFilter<string> = {
  model: sectorModel,
  value: computed(() => sectorModel.value),
  items: computed(() => sectorItems.value),
  label: 'Setor',
  field: 'sector_id',
  icon: 'fa-regular fa-bookmark',
  type: 'select',
  loading: ref(false),
  ready: sectorTypeReady.promise,
  required: false,
}

const typeFilter: DataTableFilter<string> = {
  model: typeModel,
  value: computed(() => typeModel.value),
  items: computed(() => typeItems.value),
  label: 'Tipo de Ingresso',
  field: 'type_id',
  icon: 'fa-regular fa-ticket',
  type: 'select',
  loading: ref(false),
  ready: sectorTypeReady.promise,
  required: false,
}

async function loadSectorAndTypeOptions() {
  await eventFilter.ready

  if (eventFilter.model.value === null) return

  const [sectors, types] = await Promise.all([
    new EventService().sectors(eventFilter.model.value),
    new EventService().types(eventFilter.model.value),
  ])

  sectorItems.value = sectorItems.value.concat(sectors.data.map(s => ({ title: s.name, value: s.id })))
  typeItems.value = typeItems.value.concat(types.data.map(t => ({ title: `${t.name} (${t.sector_name})`, value: t.id })))

  sectorTypeReady.resolve(true)
}

loadSectorAndTypeOptions()

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  useTextFilter({ label: 'Nome do Portador', field: 'holder_name', required: false }),
  useTextFilter({ label: 'Nome do Titular', field: 'owner_name', required: false }),
  useTextFilter({ label: 'CPF do Titular', field: 'owner_document', required: false }),
  useTextFilter({ label: 'E-mail do Titular', field: 'owner_email', required: false }),
  useTextFilter({ label: 'Código', field: 'code', icon: 'fa-regular fa-barcode', required: false }),
  useSelectFilter({
    label: 'Origem',
    field: 'origin',
    icon: 'fa-regular fa-link',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Venda Online', value: 'venda_online' },
      { title: 'Pré Impressão', value: 'pre_impressao' },
    ],
    required: false,
    initial: 'venda_online',
  }),
  sectorFilter,
  typeFilter,
])

const selectedEvent = computed(() => eventFilter.resource?.value ?? null)

const eventTitle = computed(() => selectedEvent.value?.title ?? null)

async function fetch(page: number, queryFilters: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<Reading>>> {
  try {
    if (eventFilter.model.value === null) {
      snackbar.show('Selecione um evento para buscar as leituras', 'error')
      // Matches the original's behavior: if a producer somehow has zero
      // events, there's no valid request to make. This was never type-safe
      // before either (TServersideDatatable's provider prop was untyped).
      return undefined as unknown as AxiosResponse<PaginatedResponse<Reading>>
    }

    return await new ReadingService().index(
      eventFilter.model.value,
      page,
      queryFilters,
      per_page,
    ) as unknown as AxiosResponse<PaginatedResponse<Reading>>
  } catch (error) {
    snackbar.show('Não foi possível buscar as leituras', 'error')

    throw error
  }
}
</script>
